import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Input, InputUrl, Label } from "@activecollab/components";
import { Field, Form, Formik, useFormikContext } from "formik";
import PropTypes from "prop-types";
import { LinkDialogValidator } from "~/components/TinyMCEDialogs/formValidators";

const SyncInputs = ({ sync }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (sync) {
      setFieldValue("text", values.url);
    }
  }, [setFieldValue, sync, values.url]);

  return null;
};

SyncInputs.propTypes = {
  sync: PropTypes.bool,
};

export const LinkDialog = ({ onClose, onSubmit, url, text, ...rest }) => {
  const { t } = useTranslation();

  const [textTyped, setTextTyped] = useState(false);

  const handleSubmit = useCallback(
    (value) => {
      onSubmit?.(value.url, value.text);
      onClose?.();
    },
    [onSubmit, onClose]
  );

  return (
    <Dialog
      {...rest}
      onClose={onClose}
    >
      <Formik
        initialValues={{ url, text: text || url }}
        validationSchema={LinkDialogValidator}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {() => (
          <Form role="form">
            <SyncInputs sync={!text && !textTyped} />
            <Dialog.Title>{t("Insert Link")}</Dialog.Title>
            <Dialog.ContentDivider />
            <Dialog.Content>
              <div className="tw-mb-4">
                <Field name="url">
                  {({ field, form, meta }) => (
                    <>
                      <Label
                        size="small"
                        htmlFor="url"
                        invalid={meta.error}
                        className="tw-mb-1"
                      >
                        {t("Link")}:
                      </Label>
                      <InputUrl
                        {...field}
                        id="url"
                        invalid={meta.error}
                        required
                        className="tw-w-full"
                        autoFocus
                        onChange={(value) => {
                          form.setFieldValue(field.name, value);
                        }}
                      />
                    </>
                  )}
                </Field>
              </div>
              <div className="tw-mb-4">
                <Field name="text">
                  {({ field, meta, form }) => (
                    <>
                      <Label
                        size="small"
                        htmlFor="text"
                        invalid={meta.touched && meta.error}
                        className="tw-mb-1"
                      >
                        {t("Text to Display")}:
                      </Label>
                      <Input
                        {...field}
                        type="text"
                        id="text"
                        invalid={meta.touched && meta.error}
                        required
                        className="tw-w-full"
                        onChange={(e) => {
                          setTextTyped(true);
                          form.setFieldValue(field.name, e.target.value);
                        }}
                      />
                    </>
                  )}
                </Field>
              </div>
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                type="submit"
                className="tw-mr-3"
              >
                {t("Save")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

LinkDialog.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  url: PropTypes.string,
  text: PropTypes.string,
};
